import { createRef } from 'react'
import classNames from 'classnames'
import _ from 'lodash'
import { Toast, Loader } from '@vzmi/shp_shared_compo'
import styles from './ReturnsAnalysis.css'
import safeAwait from '../../../../../lib/safeAwait'
import SPBaseModule from '../../../../lib/SPBaseModule'
import errorHelper, { httpHandler } from '../../../../../lib/errorHelper'
import {
  getAPIFormatDate
} from '../../../../lib/MomentHelper'
import Block from '../../../Common/NewDashboard/Block/Block'
import Tabs from '../../../Common/NewDashboard/Tabs/Tabs'
import NoData from '../../../Common/NoData/NoData'
import SearchForm, {
  FIELD as SEARCH_FIELD
} from './SearchForm/SearchForm'
import ReturnsChart from './ReturnsChart/ReturnsChart'
import ReturnSummaries from './ReturnSummaries/ReturnSummaries'
import {
  FIELD as TABLE_FIELD
} from './ReturnSummaries/SummariesTable/SummariesTable'
import ReturnDetailDialog from './ReturnDetailDialog/ReturnDetailDialog'
import {
  LISTING_ID_NOT_FOUND,
  NOT_FOUND
} from '../constants/noData'
import { LISTING_TYPE } from '../constants/listing'
import {
  GA_CLICK_ACTION,
  getI13nModel
} from './ReturnsAnalysis.ga'

export const MAX_REASONS = 50

export const TAB = {
  RETURNS: 'returns',
  SUMMARIES: 'summaries'
}

const TAB_MAP = {
  [TAB.RETURNS]: '金額／數量',
  [TAB.SUMMARIES]: '退貨原因'
}

export const getNoDataDescription = (formValues, isNoReturnFound) => {
  const listingType = _.get(formValues, SEARCH_FIELD.LISTING_TYPE)
  const listingId = _.toString(
    _.get(formValues, SEARCH_FIELD.LISTING_IDS)
  )
  if (isNoReturnFound) {
    if (listingType === LISTING_TYPE.ORDER && !_.isEmpty(listingId)) {
      return LISTING_ID_NOT_FOUND
    }
    return NOT_FOUND
  }
  return ''
}

export const transformValuesToSearchParams = formValues => {
  const payload = {}

  for (const field of _.keys(formValues)) {
    const fieldValue = formValues[field]
    switch (field) {
      case SEARCH_FIELD.REPORT_DATE: {
        const [start, end] = fieldValue
        _.set(payload, 'reportDateStart', getAPIFormatDate(start))
        _.set(payload, 'reportDateEnd', getAPIFormatDate(end))
        break
      }
      case SEARCH_FIELD.LISTING_IDS: {
        if (formValues[SEARCH_FIELD.LISTING_TYPE] === LISTING_TYPE.ORDER) {
          _.set(payload, field, fieldValue)
        }
        break
      }
      default: {
        break
      }
    }
  }

  return payload
}

export const transformValuesToReasonsParams = (formValues, offset) => {
  const payload = {}

  for (const field of _.keys(formValues)) {
    const fieldValue = formValues[field]
    switch (field) {
      case SEARCH_FIELD.REPORT_DATE: {
        const [start, end] = fieldValue
        _.set(payload, 'reportDateStart', getAPIFormatDate(start))
        _.set(payload, 'reportDateEnd', getAPIFormatDate(end))
        break
      }
      case 'reasonId': {
        _.set(payload, 'id', fieldValue)
        break
      }
      default: {
        break
      }
    }
  }
  _.set(payload, 'offset', offset)
  _.set(payload, 'limit', MAX_REASONS)

  return payload
}

export const getSummariesWithPercent = (summaries, sortType) => {
  const summariesSize = _.size(summaries)
  // 計算指定排序內容的總和
  const sortTypeTotal = _.reduce(summaries, (total, summary) => {
    total += _.toNumber(summary[sortType])
    return total
  }, 0)
  const {
    newSummaries: summariesWithPercent
  } = _.reduce(summaries, ({ unCalcPercent, newSummaries }, summary, index) => {
    // 最後一筆的百分比用最後扣剩的 unCalcPercent 不用再計算
    if (index === summariesSize - 1) {
      newSummaries.push({ ...summary, percent: unCalcPercent })
      return { unCalcPercent: 0, newSummaries }
    }
    // 百分比計算計算到小數點後第 2 位
    const summaryPercent = _.floor(_.toNumber(summary[sortType]) / sortTypeTotal * 100, 2)
    newSummaries.push({ ...summary, percent: summaryPercent })
    unCalcPercent = _.floor(unCalcPercent - summaryPercent, 2)
    return { unCalcPercent, newSummaries }
  }, {
    unCalcPercent: 100,
    newSummaries: []
  })
  return summariesWithPercent
}

export const checkIsNoReturnFound = summary => {
  const {
    amount,
    quantity
  } = summary
  const isNoReturnFound = (
    _.toNumber(amount) === 0 &&
    quantity === 0
  )
  return isNoReturnFound
}

export const getSortedSummaries = (summaries, sortType) => {
  const sortedSummaries = _.orderBy(
    summaries,
    summary => _.toNumber(summary[sortType]),
    'desc'
  )
  return sortedSummaries
}

class ReturnsAnalysis extends SPBaseModule {
  constructor (props) {
    super(props)
    this.state = {
      selectedTab: TAB.RETURNS,
      returns: [],
      summaries: [],
      reasonsMapping: {},
      summariesSortType: TABLE_FIELD.AMOUNT,
      returnsNoDataDescription: NOT_FOUND,
      summariesNoDataDescription: NOT_FOUND,
      isOrderSearch: false,
      isNoReturnFound: false,
      researchedFormData: {}
    }
    this.searchFormProps = null
    this.dialog = createRef()
  }

  componentDidMount () {
    this.searchFormProps.handleSubmit(this.onSearch)()
  }

  setSearchFormProps = form => (this.searchFormProps = form)

  onTabChange = tabKey => {
    this.sendPageviewByAction({
      type: GA_CLICK_ACTION.TAB,
      payload: tabKey
    })
    this.setState({ selectedTab: tabKey })
  }

  onSearch = values => this.loading(async () => {
    const isOrderSearch = (values[SEARCH_FIELD.LISTING_TYPE] === LISTING_TYPE.ORDER)
    this.setState({
      researchedFormData: values,
      isOrderSearch,
      summariesSortType: TABLE_FIELD.AMOUNT,
      selectedTab: TAB.RETURNS,
      reasonsMapping: {}
    })
    const updateStatusList = await Promise.all([
      this.updateReturnsTab(values),
      this.updateSummariesTab(values, { sortType: TABLE_FIELD.AMOUNT })
    ])
    const [errorMessage] = _.find(updateStatusList, ([error]) => error) || []
    if (errorMessage) {
      Toast.error(errorMessage)
      this.setIsLoading(false)
      return
    }

    this.setIsSuccess(true)
  })

  updateReturnsTab = async values => {
    const { fetchReturns } = this.props
    const params = transformValuesToSearchParams(values)
    const [error, payload] = await safeAwait(
      fetchReturns(params).then(httpHandler)
    )
    const errorMessage = errorHelper.parseErrorMessage(error)
    if (errorMessage) {
      return [errorMessage]
    }

    const {
      statistics: returns = [],
      summary
    } = payload
    const isNoReturnFound = checkIsNoReturnFound(summary)
    const returnsNoDataDescription = getNoDataDescription(values, isNoReturnFound)
    const convertedReturns = _.map(returns, ret => ({ ...ret, amount: +ret.amount }))
    this.setState({
      returns: convertedReturns,
      returnsNoDataDescription,
      isNoReturnFound
    })
    return [undefined, payload]
  }

  updateSummariesTab = async (values, state) => {
    const { fetchReturnReasonSummaries } = this.props
    const { sortType } = state
    const params = transformValuesToSearchParams(values)
    const [error, payload] = await safeAwait(
      fetchReturnReasonSummaries(params).then(httpHandler)
    )
    const errorMessage = errorHelper.parseErrorMessage(error)
    if (errorMessage) {
      return [errorMessage]
    }

    const {
      reasonSummaries: summaries = []
    } = payload
    const summariesNoDataDescription = getNoDataDescription(values, summaries)
    const sortedSummaries = getSortedSummaries(summaries, sortType)
    const summariesWithPercent = getSummariesWithPercent(sortedSummaries, sortType)
    this.setState({
      summaries: summariesWithPercent,
      summariesNoDataDescription
    })
    return [undefined, payload]
  }

  onSummariesSortType = async event => {
    const sortType = _.get(event, 'target.value')
    const {
      summariesSortType,
      summaries
    } = this.state
    if (sortType === summariesSortType) {
      return
    }

    this.sendPageviewByAction({
      type: GA_CLICK_ACTION.TABLE_SORT,
      payload: sortType
    })
    // 按照指定欄位排序 returnSummaries
    const sortedSummaries = getSortedSummaries(summaries, sortType)
    const summariesWithPercent = getSummariesWithPercent(sortedSummaries, sortType)
    this.setState({
      summaries: summariesWithPercent,
      summariesSortType: sortType
    })
  }

  onUpdateDetailModalVisible = isVisible => () => {
    this.setState({ isDetailModalVisible: isVisible })
  }

  onListingCountClick = async (reasonId, reason) => this.dialog.current.loading(async () => {
    const { reasonsMapping } = this.state
    const currentTotalReasons = _.get(reasonsMapping, `${reasonId}.data`, [])
    this.sendPageviewByAction({
      type: GA_CLICK_ACTION.TABLE_LINK_LISTING_COUNT
    })
    this.dialog.current.onOpen(reasonId, reason)

    // 如果指定的退貨原因已經有資料會直接被 dialog 拿來用，就不需要再從 API 取得資料
    if (!_.isEmpty(currentTotalReasons)) {
      this.dialog.current.setIsSuccess(true)
      return
    }

    const offset = _.size(currentTotalReasons)
    const [errorMessage] = await this.updateReasons(reasonId, offset)
    if (errorMessage) {
      Toast.error(errorMessage)
      this.dialog.current.setIsLoading(false)
      return
    }
    this.dialog.current.setIsSuccess(true)
  })

  updateReasons = async (reasonId, offset) => {
    const { fetchSpecificReturnReasonsListings } = this.props
    const {
      researchedFormData,
      reasonsMapping
    } = this.state
    const values = {
      ...researchedFormData,
      reasonId
    }
    const params = transformValuesToReasonsParams(values, offset)
    const [error, payload] = await safeAwait(
      fetchSpecificReturnReasonsListings(params).then(httpHandler)
    )
    const errorMessage = errorHelper.parseErrorMessage(error)
    if (errorMessage) {
      return [errorMessage]
    }

    const {
      listings: currentReasons,
      pagination: {
        resultsTotal
      }
    } = payload
    const currentTotalReasons = _.get(reasonsMapping, `${reasonId}.data`, [])
    const newReasonsMapping = _.set(reasonsMapping, reasonId, {
      data: _.concat(currentTotalReasons, currentReasons),
      resultsTotal
    })
    this.setState({ reasonsMapping: newReasonsMapping })
    return [undefined, payload]
  }

  sendPageviewByAction = (action) => {
    const i13nModel = getI13nModel(action)
    this.props.sendDashboardPageview(i13nModel)
  }

  onListingTypeChange = (e, value) => {
    this.sendPageviewByAction({
      type: GA_CLICK_ACTION.LISTING_OPTION,
      payload: value
    })
  }

  render () {
    const { className } = this.props
    const {
      isLoading,
      isOrderSearch,
      isNoReturnFound,
      selectedTab,
      returns,
      summaries,
      reasonsMapping,
      summariesSortType,
      returnsNoDataDescription,
      summariesNoDataDescription
    } = this.state
    const clz = classNames(styles.ReturnsAnalysis, {
      [className]: !!className
    })
    return (
      <div
        className={clz}
        data-role='returns-analysis'
      >
        <Loader loaded={!isLoading}>
          <Block
            key='searchForm'
            noMarginTop
          >
            <SearchForm
              onFormInit={this.setSearchFormProps}
              onSubmit={this.onSearch}
              onListingTypeChange={this.onListingTypeChange}
            />
          </Block>
          <Tabs
            key='tabs'
            activeKey={selectedTab}
            onChange={this.onTabChange}
            items={[
              {
                label: TAB_MAP[TAB.RETURNS],
                key: TAB.RETURNS,
                children: (
                  <Block noMarginBottom>
                    {
                      isNoReturnFound
                        ? (
                          <NoData
                            description={returnsNoDataDescription}
                          />
                        )
                        : (
                          <ReturnsChart data={returns} />
                        )
                    }
                  </Block>
                )
              },
              {
                label: TAB_MAP[TAB.SUMMARIES],
                key: TAB.SUMMARIES,
                children: (
                  <Block noMarginBottom>
                    {
                      _.isEmpty(summaries)
                        ? (
                          <NoData
                            description={summariesNoDataDescription}
                          />
                        )
                        : (
                          <ReturnSummaries
                            data={summaries}
                            isOrderSearch={isOrderSearch}
                            onSortChange={this.onSummariesSortType}
                            sortType={summariesSortType}
                            onListingCountClick={this.onListingCountClick}
                          />
                        )
                    }
                  </Block>
                )
              }
            ]}
          />
          <ReturnDetailDialog
            key='dialog'
            ref={this.dialog}
            reasonsMapping={reasonsMapping}
            updateReasons={this.updateReasons}
          />
        </Loader>
      </div>
    )
  }
}

export default ReturnsAnalysis
